import React, {useEffect, useState} from 'react';
import {useIntl} from 'react-intl';

import ProductModel from 'coreClasses/product/ProductModel';
import {useConfiguration, useGeolocalizedSizes, useProduct, useSetUI, useUI} from 'coreHooks';

import {useGeneralProvider} from 'brandComponents/_app-config/GeneralProvider';
import Button from 'brandComponents/Button';
import Field from 'brandComponents/Form/Subcomponents/Field';
import SizeSelectorButton from 'brandComponents/SizeSelectorButton';

import {updateVariantId} from '../../utils';

import {IExchangeButtonsProps} from './IExchangeButtons';
import messages from './messages';

import styles from './ExchangeButtons.module.scss';

const ExchangeButtons = ({product, itemKey, shippingCountryCode}: IExchangeButtonsProps) => {
	const intl = useIntl();
	const setUI = useSetUI();
	const pid = product?.getProductId();
	const [selectedPID, setSelectedPID] = useState<string>(pid);
	const selectedProduct = useProduct(selectedPID);
	const {managerRepository} = useGeneralProvider();
	const sitePreferences = useConfiguration('sitePreferences');
	const disableExchangeForPreorder = sitePreferences.disableExchangeForPreorder || false;
	const originalGroupId = product?.getVariationGroupId(pid);
	const [variationGroupId, setVariationGroupId] = useState(originalGroupId);
	const variationGroup: ProductModel = useProduct(variationGroupId);
	const variantId = variationGroup.getVariant(selectedPID)?.getProductId();

	const variationAttributes = product?.getProductObject().variationAttributes;

	const masterProduct = useProduct(product.getMasterProductId());

	const colors = variationAttributes.find(variation => variation.id === 'color').values;
	const sizes = variationAttributes.find(variation => variation.id === 'size').values;

	const colorSelectorSelectedPID = useUI('colorSelectorSelectedPID');
	const sizeSelectorSelectedPID = useUI('sizeSelectorSelectedPID');
	const sizeSelector = useUI('sizeSelector');

	const [selectedColor, setSelectedColor] = useState(undefined);
	const [selectedSize, setSelectedSize] = useState(undefined);
	const [listenPanelChange, setListenPanelChange] = useState(false);

	const newSelectedColor = useProduct(selectedColor);
	const newSelectedSize = useProduct(selectedSize);

	useEffect(() => {
		if (listenPanelChange && colorSelectorSelectedPID && masterProduct.getVariationGroupIds().includes(colorSelectorSelectedPID)) {
			setSelectedPID(colorSelectorSelectedPID);
			setSelectedColor(colorSelectorSelectedPID);
			setSelectedSize(undefined);
			setVariationGroupId(colorSelectorSelectedPID);

			managerRepository.getUiMgr().resetSizeSelectorSelectedPid();
		}

		if (sizeSelectorSelectedPID && listenPanelChange) {
			setSelectedPID(sizeSelectorSelectedPID);
			setSelectedSize(sizeSelectorSelectedPID);
			setListenPanelChange(false);
		}
	}, [colorSelectorSelectedPID, sizeSelectorSelectedPID, sizeSelector]);

	const colorSelectorLabel = newSelectedColor.getColor() !== undefined
		? `${intl.formatMessage(messages['colorSelectorSelected'])}: ${newSelectedColor.getColorDescription()}`
		: product
			? `${intl.formatMessage(messages['colorSelectorSelected'])}: ${product.getColorDescription()}`
			: intl.formatMessage(messages['colorSelector']);

	const geolocalizedSize = useGeolocalizedSizes(newSelectedSize, {countryCode: shippingCountryCode});
	const sizeSelectorLabel = newSelectedSize.getSize() !== undefined
		? `${intl.formatMessage(messages['sizeSelectorSelected'])}: ${geolocalizedSize[newSelectedSize.getProductId()]}`
		: intl.formatMessage(messages['sizeSelector']);

	useEffect(() => {
		if (pid !== selectedPID) {
			setSelectedPID(pid);
		}
	}, [pid]);

	const handleColorSelectorClick = () => {
		setUI.showColorSelector(variationGroupId, variantId);
		setListenPanelChange(true);
	};

	return (
		<div className={styles['return-create-step-1__exchange--buttons']}>
			<div className={styles['return-create-step-1__exchange--buttons-color']}>
				{colors.length > 1 && (
					<Button
						classNames="product_color_selector"
						onClick={handleColorSelectorClick}
						variant="primary"
					>
						{colorSelectorLabel}
					</Button>
				)}
				<Field
					key={`exchangeColor-${itemKey}`}
					name={`exchangeColor-${itemKey}`}
					component="input"
					type="hidden"
					initialValue={colorSelectorSelectedPID || newSelectedColor.getColor()}
				/>
			</div>

			<div className={styles['return-create-step-1__exchange--buttons-size']}>
				{sizes.length > 1 && (
					<SizeSelectorButton
						variationGroupId={variationGroupId}
						variantId={variantId}
						label={sizeSelectorLabel}
						variant="primary"
						countryCode={shippingCountryCode}
						hidePreorderSizes={disableExchangeForPreorder}
						handleClick={() => {
							setListenPanelChange(true);
						}}
					/>
				)}
				<Field
					key={`exchangeSize-${itemKey}`}
					name={`exchangeSize-${itemKey}`}
					component="input"
					type="hidden"
					initialValue={sizeSelectorSelectedPID || newSelectedSize.getSize()}
				/>
			</div>

			<Field
				key={`exchangeItem-${itemKey}`}
				name={`exchangeItem-${itemKey}`}
				component="input"
				type="hidden"
				initialValue={selectedProduct.isVariant()
					? selectedProduct.getProductId()
					: updateVariantId(selectedProduct as ProductModel, sizes.length)
				}
			/>
		</div>
	);
};

export default ExchangeButtons;